import { accountsAuthUrl, accountsUrl } from '../config/api';
import { webDeviceFields } from '../auth/webDevice';
import type {
  ApiEnvelope,
  ApiUserResponse,
  AuthDeviceOption,
  CompleteDeviceLoginRequest,
  DeviceLimitChallenge,
  GoogleLoginRequest,
  GoogleProfile,
  LoginRequest,
  SignupRequest,
  UserProfile,
} from '../auth/types';
import { AuthRequestError } from '../auth/types';
import { postJson, RequestFailure } from './httpClient';

export const DEVICE_LIMIT_CODE = 'DEVICE_LIMIT_REACHED';

function normalizeApiId(id: ApiUserResponse['_id']): string {
  if (typeof id === 'string') {
    return id;
  }
  if (id && typeof id === 'object' && '$oid' in id) {
    return String((id as { $oid: string }).$oid);
  }
  return String(id ?? '');
}

function mapUser(dto: ApiUserResponse): UserProfile {
  const userName = dto.user_name?.trim() || dto.google_name?.trim() || '';
  const userEmail = dto.user_email?.trim() || dto.google_mail?.trim() || '';

  return {
    id: normalizeApiId(dto._id),
    loginType: dto.login_type ?? '',
    userName,
    userEmail,
    googleMail: dto.google_mail?.trim() || '',
    userPhone: dto.user_phone ?? '',
    developer: Boolean(dto.developer),
    token: dto.token ?? '',
    playerId: dto.player_id ?? '',
  };
}

function toAuthError(error: unknown): AuthRequestError {
  if (error instanceof RequestFailure) {
    const kind =
      error.kind === 'server'
        ? 'server'
        : error.kind === 'parse'
          ? 'parse'
          : 'network';
    return new AuthRequestError(kind, error.message);
  }
  if (error instanceof Error) {
    return new AuthRequestError('unknown', error.message);
  }
  return new AuthRequestError(
    'unknown',
    'Something went wrong. Please try again.'
  );
}

function deviceLabel(device: Record<string, unknown>): string {
  const name = String(device.display_name || '').trim();
  if (name) return name;
  const model = String(device.device_model || '').trim();
  const platform = String(device.platform || '').trim();
  const combined = [model, platform].filter(Boolean).join(' · ');
  return combined || 'Signed-in device';
}

export function readDeviceLimit(
  envelope: ApiEnvelope<unknown>
): DeviceLimitChallenge | null {
  if (envelope.result !== 0 || envelope.error_code !== DEVICE_LIMIT_CODE) {
    return null;
  }
  const payload = envelope.response;
  if (!payload || Array.isArray(payload) || typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const claimToken = String(record.claim_token || '').trim();
  if (!claimToken || !Array.isArray(record.devices)) {
    return null;
  }
  const devices: AuthDeviceOption[] = [];
  record.devices.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const device = item as Record<string, unknown>;
    const sessionId = String(device.session_id || '').trim();
    if (!sessionId) return;
    devices.push({ sessionId, label: deviceLabel(device) });
  });
  if (devices.length === 0) {
    return null;
  }
  return {
    message:
      envelope.message || 'Device limit reached. Choose a device to sign out.',
    claimToken,
    devices,
  };
}

function throwIfDeviceLimit(envelope: ApiEnvelope<unknown>): void {
  const limit = readDeviceLimit(envelope);
  if (!limit) return;
  throw new AuthRequestError('api', limit.message, DEVICE_LIMIT_CODE, limit);
}

export function isDeviceLimitError(
  error: unknown
): error is AuthRequestError & { details: DeviceLimitChallenge } {
  if (!(error instanceof AuthRequestError) || error.code !== DEVICE_LIMIT_CODE) {
    return false;
  }
  const details = error.details;
  return Boolean(
    details &&
      details.claimToken &&
      details.devices.length > 0
  );
}

async function handleEnvelope(
  envelope: ApiEnvelope
): Promise<UserProfile> {
  throwIfDeviceLimit(envelope);
  if (envelope.result === 0) {
    throw new AuthRequestError(
      'api',
      envelope.message || 'Request failed. Please try again.'
    );
  }
  if (!envelope.response) {
    throw new AuthRequestError(
      'api',
      'Account data was missing from the server response.'
    );
  }
  return mapUser(envelope.response);
}

export async function login(
  email: string,
  password: string,
  playerId: string,
  signal?: AbortSignal
): Promise<UserProfile> {
  const body: LoginRequest = {
    type: 'NORMAL_LOGIN',
    user_email: email.trim(),
    password,
    player_id: playerId,
    ...webDeviceFields(playerId),
  };

  try {
    // v2 checks bcrypt passwords. v1 only matches plaintext and rejects app accounts.
    const envelope = await postJson(accountsAuthUrl(), body, signal);
    return await handleEnvelope(envelope);
  } catch (error) {
    if (error instanceof AuthRequestError) {
      throw error;
    }
    throw toAuthError(error);
  }
}

type SignupInput = {
  user_name: string;
  user_email: string;
  user_phone: string;
  password: string;
  player_id: string;
  is_developer: boolean;
};

export async function signup(
  payload: SignupInput,
  signal?: AbortSignal
): Promise<UserProfile> {
  const body: SignupRequest = {
    type: 'NORMAL_SIGNUP',
    ...webDeviceFields(payload.player_id),
    ...payload,
  };

  try {
    const envelope = await postJson(accountsAuthUrl(), body, signal);
    return await handleEnvelope(envelope);
  } catch (error) {
    if (error instanceof AuthRequestError) {
      throw error;
    }
    throw toAuthError(error);
  }
}

export async function completeDeviceLogin(
  claimToken: string,
  revokeSessionId: string,
  playerId: string,
  signal?: AbortSignal
): Promise<UserProfile> {
  const body: CompleteDeviceLoginRequest = {
    type: 'COMPLETE_DEVICE_LOGIN',
    claim_token: claimToken,
    revoke_session_id: revokeSessionId,
    player_id: playerId,
    ...webDeviceFields(playerId),
  };

  try {
    const envelope = await postJson(accountsAuthUrl(), body, signal);
    return await handleEnvelope(envelope);
  } catch (error) {
    if (error instanceof AuthRequestError) {
      throw error;
    }
    throw toAuthError(error);
  }
}

export async function googleLogin(
  profile: GoogleProfile,
  playerId: string,
  isDeveloper: boolean,
  signal?: AbortSignal
): Promise<UserProfile> {
  const body: GoogleLoginRequest = {
    type: 'GOOGLE',
    social_id: profile.socialId,
    player_id: playerId,
    google_name: profile.googleName,
    google_mail: profile.googleMail,
    google_photo: profile.googlePhoto,
    user_phone: '',
    developer: isDeveloper ? 1 : 0,
  };

  try {
    // Stay on v1. v2 Google login requires an id_token; this flow already works.
    const envelope = await postJson(accountsUrl(), body, signal);
    return await handleEnvelope(envelope);
  } catch (error) {
    if (error instanceof AuthRequestError) {
      throw error;
    }
    throw toAuthError(error);
  }
}

export function authErrorTitle(
  kind: AuthRequestError['kind'],
  context: 'login' | 'signup' | 'google' = 'login'
): string {
  switch (kind) {
    case 'network':
      return 'No connection';
    case 'server':
      return 'Server unavailable';
    case 'parse':
      return 'Unexpected response';
    case 'api':
      if (context === 'signup') return 'Sign up failed';
      if (context === 'google') return 'Google sign-in failed';
      return 'Sign in failed';
    default:
      return 'Something went wrong';
  }
}
